import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';

import {
  glacierPolicyApiCloseRenewalMutation,
  glacierPolicyApiGetChildPolicyQueryKey,
  glacierPolicyApiGetPolicyTermsQueryKey,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import type { ChildPolicyData } from '@/lib/heyapi/types.gen';

import { formatErrorMessage } from '../../../utils';
import type { Message } from '../../StandardMessage';

export const CloseRenewal = ({
  accountId,
  isOpen,
  onOpenChange,
  setMessage,
  renewalPolicyData,
}: {
  accountId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  setMessage: (message: Message | undefined) => void;
  renewalPolicyData: ChildPolicyData | null;
}) => {
  const queryClient = useQueryClient();

  const renewalPolicyQK = glacierPolicyApiGetChildPolicyQueryKey({
    path: {
      account_id: accountId,
    },
  });

  // Closing the renewal drops a term from the chain, and every term in that
  // chain caches the same list under its own account_id. Dropping `path` from
  // the key matches the endpoint for *all* policies, so the Policy Terms
  // selector can't keep listing the closed renewal on another term (DC-3643).
  const [{ path: _path, ...policyTermsQK }] =
    glacierPolicyApiGetPolicyTermsQueryKey({ path: { account_id: accountId } });

  const { mutate: closeRenewal, isPending: isClosingRenewal } = useMutation({
    ...glacierPolicyApiCloseRenewalMutation(),
    onSuccess() {
      setMessage(undefined);
      onOpenChange(false);
      // Invalidate parent policy query to get updated renewal status
      queryClient.invalidateQueries({ queryKey: renewalPolicyQK });
      queryClient.invalidateQueries({ queryKey: [policyTermsQK] });
      toast({
        title: 'Policy Closed',
        description: (
          <div>
            <p>Policy closed successfully</p>
          </div>
        ),
      });
    },
    onError(error) {
      setMessage(undefined);
      onOpenChange(false);
      toast({
        title: 'Error Closing Policy',
        description: formatErrorMessage(
          error,
          'An error occurred while closing the policy'
        ),
      });
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Close Renewal</DialogTitle>
          <DialogDescription>
            Are you sure you want to close the renewal?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={isClosingRenewal}
            loading={isClosingRenewal}
            onClick={() => {
              setMessage({
                content: 'Closing renewal...',
                level: 'INFO',
              });
              closeRenewal({
                path: { account_id: renewalPolicyData?.policyId || '' },
              });
            }}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
